/**
 * Client-side helper to send email with PDF attachment and uploaded-file links via Netlify function
 * @param pdfFile - The PDF file to attach
 * @param formTitle - The title of the form
 * @param formData - The form structure data (sections, fields, etc.)
 * @param formInputs - The form input values
 * @param uploadedFilesByField - Uploaded file metadata grouped by field key
 * @returns Promise with the email result
 */
export async function sendEmail(
  pdfFile: File,
  formTitle: string,
  formData?: {
    title?: string | null;
    section?: Array<{
      _key?: string | null;
      title?: any;
      formFields?: Array<any | null> | null;
    } | null> | null;
  },
  formInputs?: Record<string, unknown>,
  uploadedFilesByField?: Record<
    string,
    Array<{
      id: string;
      fileName: string;
      mimeType: string;
      size: number;
      downloadUrl?: string;
      storagePath?: string;
    }>
  >,
): Promise<{
  success: boolean;
  message?: string;
  error?: string;
}> {
  try {
    // Convert PDF file to base64 for transmission
    const base64Pdf = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // Remove data URL prefix (data:application/pdf;base64,)
        const base64 = (reader.result as string).split(",")[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(pdfFile);
    });

    // Find submitter's email from form inputs (look for email type fields)
    let submitterEmail: string | null = null;
    if (formData && formInputs) {
      // Search through all sections and form fields to find email type fields
      const sections = formData.section || [];
      for (const section of sections) {
        if (!section?.formFields) continue;
        for (const field of section.formFields) {
          if (field && field.type === "email" && field.id) {
            const emailValue = formInputs[field.id];
            if (emailValue && typeof emailValue === "string" && emailValue.trim() !== "") {
              submitterEmail = emailValue.trim();
              break;
            }
          }
        }
        if (submitterEmail) break;
      }
    }

    const uploadedFileLinks = Object.entries(uploadedFilesByField || {}).flatMap(
      ([fieldKey, files]) =>
        files
          .filter(file => file.downloadUrl)
          .map(file => ({
            fieldKey,
            fileName: file.fileName,
            mimeType: file.mimeType,
            size: file.size,
            url: file.downloadUrl as string,
            storagePath: file.storagePath || "",
          })),
    );

    // Call Netlify function (server-side email sending with nodemailer)
    const response = await fetch("/.netlify/functions/sendEmail", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        pdfFile: base64Pdf,
        pdfFileName: pdfFile.name,
        formTitle: formTitle || "Form Submission",
        formData: formData || null,
        formInputs: formInputs || null,
        uploadedFileLinks,
        submitterEmail: submitterEmail || null,
      }),
    });

    // Get response text first to check if it's valid JSON
    const responseText = await response.text();
    // Handle 404 - function not found (likely running gatsby develop instead of netlify dev)
    if (response.status === 404) {
      console.warn("Netlify function not found. Make sure you're running 'netlify dev' instead of 'gatsby develop'");
      return {
        success: false,
        error: "Email service unavailable. Please use 'netlify dev' to run the development server.",
      };
    }

    let result;
    try {
      result = JSON.parse(responseText);
    } catch (parseError) {
      console.error("Failed to parse response as JSON:", parseError);
      return {
        success: false,
        error: `Invalid response from server: ${responseText.substring(0, 100)}`,
      };
    }

    if (!response.ok) {
      return {
        success: false,
        error: result.error || result.message || "Failed to send email",
      };
    }

    return {
      success: true,
      message: result.message || "Email sent successfully",
    };
  } catch (error) {
    console.error("Error in sendEmail:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
}
